import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Subject,
  takeUntil,
  finalize,
  filter,
  mergeMap,
  switchMap,
  Observable,
  shareReplay,
  catchError,
  EMPTY,
} from 'rxjs';
import moment from 'moment';

import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { ClientService } from '@app/core/services/client.service';
import { NotificationService } from '@app/core/services/notification.service';
import { Client } from '@app/core/types/client';
import {
  ID_VERIFICATION_STATE_ICON,
  ID_VERIFICATION_STATE_CLASS,
  PROCESSOR_COLOR,
  IdentityVerification,
  IdentityVerificationEvent,
  ID_VERIFICATION_STATE,
  VERIFICATION_FLOW,
  StartVerificationDialogData,
  FLOW_OPTIONS,
  VerificationDecision,
  FlowOption,
  eventType,
  PROCESSOR,
} from '@app/core/types/identity-verification';
import { KycState, KycStates } from '@app/core/types/kyc-state';
import { IdVerificationSocket } from '@app/main/socket-io/id-verification-socket.service';
import { StartVerificationDialogComponent } from '../start-identity-verification-dialog/start-identity-verification-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { VerificationVideoModalComponent } from '../verification-video-modal-component/verification-video-modal.component';
import { ShuftiRequestService } from '@app/core/services/shufti-request.service';
import { AdministratorService } from '@app/core/services/administrator.service';
import { Administrator } from '@app/core/types/administrator';

interface VerificationListItem {
  id: number;
  state: ID_VERIFICATION_STATE;
  startedAt: string;
  stateClass: string;
  flow: FlowOption;
  brand: string;
}

interface EventView {
  id: number;
  type: string;
  typeDisplay: string;
  processor: string;
  reference: string;
  createdAt: string;
  data: Record<string, unknown> | null;
  dotColor: string;
  isFail: boolean;
  isSuccess: boolean;
  procColor: string;
  procColorBg: string;
  dataEntries: [string, unknown][];
  hasData: boolean;
}

interface VerificationDetailView {
  id: number;
  state: ID_VERIFICATION_STATE;
  stateClass: string;
  flow: VERIFICATION_FLOW;
  brand: string;
  startedAt: string;
  endedAt: string | null;
  duration: string;
  miReportId: number | null;
  events: EventView[];
}

@Component({
  selector: 'itfg-identity-verification',
  templateUrl: './identity-verification.component.html',
  styleUrl: './identity-verification.component.scss',
  standalone: false,
})
export class IdentityVerificationComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() client: Client;

  public STATE_ICON = ID_VERIFICATION_STATE_ICON;
  public STATE_CLASS = ID_VERIFICATION_STATE_CLASS;
  public PROC_COLOR = PROCESSOR_COLOR;
  public FLOW_OPTIONS = FLOW_OPTIONS;
  public verficationDecisions: typeof VerificationDecision =
    VerificationDecision;

  public verifications: IdentityVerification[] = [];
  public verificationItems: VerificationListItem[] = [];
  public currentIndex: number = 0;
  public currentDetail: IdentityVerification | null = null;
  public detailView: VerificationDetailView | null = null;

  public loadingList: boolean = false;
  public loadingDetail: boolean = false;
  public expanded: boolean = false;
  public verificationStartedFromHere: boolean = false;
  public selectedEventId: string | number | null = null;
  public newVerificationIds: Set<number> = new Set<number>();

  public unsubscribe = new Subject<void>();

  private videoBlob$?: Observable<Blob>;
  public videoReady: boolean | null = null;
  private administrators: Administrator[];

  constructor(
    public service: IdentityReportsService,
    public clientService: ClientService,
    public notificationService: NotificationService,
    public idVerificationSocket: IdVerificationSocket,
    public dialog: MatDialog,
    public translate: TranslateService,
    public dialogProvider: DialogProviderService,
    public viewContainerRef: ViewContainerRef,
    private shuftiRequestService: ShuftiRequestService,
    private administratorService: AdministratorService
  ) { }

  ngOnInit() {
    this.clientService.loadKycStates();

    this.idVerificationSocket
      .onVerificationUpdated()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((verification: IdentityVerification) => {
        const previousState = this.currentDetail?.state;

        this.currentDetail = verification;
        this.detailView = this.buildDetailView(verification);
        this.service.identityVerificationDetail$.next(verification);
        this.loadList();

        if (previousState !== verification.state && this.client?.id) {
          if (verification.state === ID_VERIFICATION_STATE.SUCCESS) {
            this.clientService
              .getClientById(this.client.id)
              .pipe(takeUntil(this.unsubscribe))
              .subscribe((client: Client) => {
                this.clientService.setCurrentClient(client);
              });
          } else {
            this.applyKycPatchFromVerification(verification.state);
          }
        }
      });

    this.idVerificationSocket
      .onVerificationAdded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((verification: IdentityVerification) => {
        if (!this.verificationStartedFromHere) {
          this.verifications = [verification, ...this.verifications];
          this.verificationItems = this.verifications.map((v) =>
            this.buildListItem(v)
          );
          this.currentIndex += 1;
          this.newVerificationIds.add(verification.id);

          if (this.client?.id) {
            this.applyKycPatchFromVerification(verification.state);
          }
        }
      });

    this.administratorService.administrators$
      .pipe(takeUntil(this.unsubscribe),)
      .subscribe((administrators: Administrator[]) => {
        this.administrators = administrators;
      });
  }

  ngOnChanges(): void {
    if (this.client?.id && this.client?.civilId) {
      this.service.identityVerificationDetail$.next(undefined);
      this.idVerificationSocket.joinClientRoom(
        this.client.civilId,
        this.client.brand.code
      );
      this.loadList();
    }
  }

  applyKycPatchFromVerification(state: ID_VERIFICATION_STATE): void {
    const derived = this.deriveKycStateName(state);
    if (!derived) return;

    const allStates = this.clientService.kycStatesSubject.getValue();
    const match = allStates.find((s: KycState) => s.name === derived);
    if (!match) return;

    this.clientService.patchCurrentClientKycState(this.client.id, match);
  }

  deriveKycStateName(state: ID_VERIFICATION_STATE): KycStates | null {
    switch (state) {
      case ID_VERIFICATION_STATE.SUCCESS:
        return KycStates.VALID;
      case ID_VERIFICATION_STATE.FAIL:
        return KycStates.INVALID;
      case ID_VERIFICATION_STATE.INITIALIZED:
        return KycStates.INVALID_IN_PROGRESS;
      case ID_VERIFICATION_STATE.IN_PROGRESS:
        return KycStates.INVALID_IN_PROGRESS;
      case ID_VERIFICATION_STATE.IN_REVIEW:
        return KycStates.INVALID_IN_REVIEW;
      default:
        return KycStates.INVALID_MISSING;
    }
  }

  get activeVerificationsCount(): number {
    return this.verifications.filter(
      (v: IdentityVerification) =>
        v.state === ID_VERIFICATION_STATE.IN_PROGRESS ||
        v.state === ID_VERIFICATION_STATE.IN_REVIEW ||
        v.state === ID_VERIFICATION_STATE.INITIALIZED
    ).length;
  }

  get currentVerificationIsActive(): boolean {
    const s = this.currentDetail?.state;
    return (
      s === ID_VERIFICATION_STATE.IN_PROGRESS ||
      s === ID_VERIFICATION_STATE.IN_REVIEW
    );
  }

  /**
   * The list spans brands, so the operator can be looking at a verification that belongs to a
   * different brand's user. Deciding it would move that other user's kyc state while this panel
   * patched the chip of the client on screen, so anything that writes is read-only here.
   */
  get currentVerificationIsForeign(): boolean {
    const brand = this.currentDetail?.brand;
    return !!brand && brand !== this.client?.brand?.code;
  }

  /** Newest first, like the list itself. */
  get ownBrandVerifications(): IdentityVerification[] {
    return this.verifications.filter(
      (v: IdentityVerification) => v.brand === this.client?.brand?.code
    );
  }

  get enableShowVideoInMatMenu(): boolean {
    return (
      !!this.currentDetail &&
      this.currentDetail?.state !== ID_VERIFICATION_STATE.INITIALIZED &&
      this.currentDetail?.flow === VERIFICATION_FLOW.SHUFTI &&
      this.videoReady
    );
  }

  flowOptionFor(flow: VERIFICATION_FLOW) {
    return (
      this.FLOW_OPTIONS.find((opt: FlowOption) => opt.value === flow) ?? null
    );
  }

  eventById(events: EventView[], id: string | number): EventView | null {
    return events.find((ev: EventView) => ev.id === id) ?? null;
  }

  /**
   * Reloads the list without moving the operator off the verification they are reading. A reload is
   * triggered by every socket update - uploading a document to the active verification causes one -
   * so defaulting to the newest row would silently change what is on screen mid-task.
   *
   * @param selectVerificationId select this verification instead of the one currently open; used
   *        after starting a new verification, which should become the selected one.
   */
  loadList(selectVerificationId?: number): void {
    const targetId = selectVerificationId ?? this.currentDetail?.id ?? null;

    this.loadingList = true;
    this.verifications = [];
    this.verificationItems = [];
    this.currentIndex = 0;
    this.currentDetail = null;
    this.detailView = null;

    this.service
      .getIdentityVerificationList(this.client.civilId)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.loadingList = false))
      )
      .subscribe({
        next: (data: IdentityVerification[]) => {
          this.verifications = data;
          this.verificationItems = data.map((v) => this.buildListItem(v));

          if (!data.length) {
            this.service.identityVerificationDetail$.next(null);
            return;
          }

          const keptIndex = data.findIndex(
            (v: IdentityVerification) => v.id === targetId
          );
          this.currentIndex = keptIndex >= 0 ? keptIndex : 0;
          this.loadDetail(data[this.currentIndex].id);
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'verification.id.loadListError',
          });
        },
      });
  }

  loadDetail(id: number): void {
    this.loadingDetail = true;

    this.service
      .getIdentityVerificationReport(id)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.loadingDetail = false))
      )
      .subscribe({
        next: (data: IdentityVerification) => {
          if (this.currentDetail) {
            this.idVerificationSocket.leaveVerificationRoom(
              this.currentDetail.id
            );
          }
          this.currentDetail = data;
          this.prefetchVideo();
          this.detailView = this.buildDetailView(data);
          this.idVerificationSocket.joinVerificationRoom(this.currentDetail.id);
          this.service.identityVerificationDetail$.next(data);
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'verification.id.loadDetailError',
          });
        },
      });
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  toggleEvent(id: string | number): void {
    this.selectedEventId = this.selectedEventId === id ? null : id;
  }

  private prefetchVideo(): void {
    this.videoReady = null;

    this.videoBlob$ = this.service
      .getVerificationVideoUrl$(this.currentDetail.id)
      .pipe(
        switchMap(({ url, accessToken }) =>
          this.shuftiRequestService.getVerificationVideo$(url, accessToken)
        ),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.videoBlob$
      .pipe(
        takeUntil(this.unsubscribe),
        catchError(() => {
          this.videoReady = false;
          return EMPTY;
        })
      )
      .subscribe({
        next: () => (this.videoReady = true),
        error: () => {
          this.videoReady = false;
        },
      });
  }

  openVideoModal(event): void {
    event.stopPropagation();

    this.videoBlob$?.pipe(takeUntil(this.unsubscribe)).subscribe((blob) => {
      const videoUrl = URL.createObjectURL(blob);
      this.dialog
        .open(VerificationVideoModalComponent, {
          data: { videoUrl },
          width: '720px',
          panelClass: 'video-dialog',
        })
        .afterClosed()
        .subscribe(() => URL.revokeObjectURL(videoUrl));
    });
  }

  startVerification(): void {
    // the list spans brands, but a new verification - and the active one it replaces, and the
    // documents it inherits - only ever belong to the client on screen
    const latestVerification = this.ownBrandVerifications[0] ?? null;

    const activeVerification =
      latestVerification &&
        (latestVerification.state === ID_VERIFICATION_STATE.IN_PROGRESS ||
          latestVerification.state === ID_VERIFICATION_STATE.IN_REVIEW ||
          latestVerification.state === ID_VERIFICATION_STATE.INITIALIZED)
        ? latestVerification
        : null;

    const data: StartVerificationDialogData = {
      client: this.client,
      previousVerification: latestVerification,
      activeVerification,
    };

    this.verificationStartedFromHere = true;

    this.dialog
      .open<
        StartVerificationDialogComponent,
        StartVerificationDialogData,
        IdentityVerification | null
      >(StartVerificationDialogComponent, {
        data,
        width: '720px',
        maxWidth: '90vw',
        autoFocus: false,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((verification) => {
        this.verificationStartedFromHere = false;
        if (!verification) return;
        this.loadList(verification.id);

        if (this.client?.id) {
          this.applyKycPatchFromVerification(verification.state);
        }
      });
  }

  submitDecision(action: VerificationDecision): void {
    const verification = this.verifications[this.currentIndex];
    if (!verification) return;
    if (this.currentVerificationIsForeign) return;

    const translatedAction = this.translate.instant(
      'verification.id.state.' + action.toUpperCase()
    );

    const config = {
      message: this.translate.instant(
        'verification.id.decisionConfirmMessage',
        {
          action: translatedAction,
        }
      ),
      disableClose: false,
      viewContainerRef: this.viewContainerRef,
      title: this.translate.instant('verification.id.decisionConfirmTitle', {
        action: translatedAction,
      }),
      acceptButton: this.translate.instant('global.confirm'),
      cancelButton: this.translate.instant('global.cancel'),
    };

    this.dialogProvider
      .openConfirm(config)
      .afterClosed()
      .pipe(
        filter((accept) => accept === true),
        mergeMap(() =>
          this.service.changeVerificationStatus$(verification.id, action)
        ),
        switchMap(() => this.clientService.getClientById(this.client.id)),
        takeUntil(this.unsubscribe)
      )
      .subscribe({
        next: () => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText:
              action === VerificationDecision.SUCCESS
                ? 'verification.id.validateSuccess'
                : 'verification.id.invalidateSuccess',
          });

          if (this.client?.id) {
            const targetState =
              action === VerificationDecision.SUCCESS
                ? ID_VERIFICATION_STATE.SUCCESS
                : ID_VERIFICATION_STATE.FAIL;
            this.applyKycPatchFromVerification(targetState);
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText:
              action === VerificationDecision.SUCCESS
                ? 'verification.id.validateError'
                : 'verification.id.invalidateError',
          });
        },
      });
  }

  selectVerification(index: number): void {
    if (index === this.currentIndex) return;
    this.currentIndex = index;
    this.loadDetail(this.verifications[index].id);
  }

  buildListItem(v: IdentityVerification): VerificationListItem {
    return {
      id: v.id,
      state: v.state,
      startedAt: v.startedAt,
      stateClass: this.STATE_CLASS[v.state] ?? 'expired',
      flow: this.flowOptionFor(v.flow),
      brand: v.brand,
    };
  }

  buildDetailView(v: IdentityVerification): VerificationDetailView {
    return {
      id: v.id,
      state: v.state,
      stateClass: this.STATE_CLASS[v.state] ?? 'expired',
      flow: v.flow,
      brand: v.brand,
      startedAt: v.startedAt,
      endedAt: v.endedAt,
      duration: this.computeDuration(v.startedAt, v.endedAt),
      miReportId: v.miReportId,
      events: (v.events ?? []).map((ev) => this.buildEventView(ev)),
    };
  }

  getEventTranslationKey(event: IdentityVerificationEvent) {
    if (
      [
        eventType.VERIFICATION_INITIALIZED,
        eventType.SHUFTI_ONSITE_INITIALIZED,
        eventType.SHUFTI_ONSITE_ENDED,
        eventType.SHUFTI_ONSITE_PARSED,
        eventType.VERIFICATION_IN_PROGRESS,
        eventType.VERIFICATION_IN_REVIEW,
        eventType.VERIFICATION_FAIL,
        eventType.VERIFICATION_SUCCESS,
        eventType.MI_REPORT_PROVISIONED,
        eventType.THEIA_CROSS_VALIDATION_ERROR,
      ].includes(event.type)
    ) {
      return event.type;
    }

    if (event.type === eventType.SHUFTI_ONSITE_CALLBACK) {
      return event.type;
    }

    if (
      [
        eventType.REJECTED,
        eventType.PROVIDED,
        eventType.VALIDATED,
        eventType.ACCEPTED,
        eventType.CROSS_VALIDATION,
        eventType.SIMPLE_VALIDATION,
        eventType.DOCUMENT_PROVISIONED,
      ].includes(event.type)
    ) {
      if (event.data?.documentType) {
        return `${event.type}_${event.data?.documentType}`;
      }
    }

    return event.type;
  }

  getProcessor(processor: string, reference: string) {
    if (processor === PROCESSOR.OPERATOR) {
      const administrator = this.administrators?.find(el => Number(el?.id) === Number(reference));

      if (!administrator) {
        return null;
      }

      return administrator.email;
    }
    return null;
  }

  buildEventView(ev: IdentityVerificationEvent): EventView {
    const isFail = /FAIL|REJECT|ERROR/.test(ev.type);
    const isSuccess = /SUCCESS|APPROVE|COMPLETE|MATCH/.test(ev.type);
    const procColor = this.PROC_COLOR[ev.processor] ?? '#546e7a';
    const dataEntries = this.parseEventDataEntries(ev);

    return {
      id: ev.id,
      type: ev.type,
      typeDisplay: this.getEventTranslationKey(ev),
      processor: ev.processor,
      reference: this.getProcessor(ev.processor, ev.reference),
      createdAt: ev.createdAt,
      data: ev.data,
      dotColor: isFail ? '#e53935' : isSuccess ? '#43a047' : procColor,
      isFail,
      isSuccess,
      procColor,
      procColorBg: procColor + '14',
      dataEntries,
      hasData: dataEntries.length > 0,
    };
  }

  computeDuration(startedAt: string, endedAt: string | null): string {
    if (!endedAt) return 'ongoing';
    const totalSeconds = Math.round(
      moment(endedAt).diff(moment(startedAt), 'seconds', true)
    );
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }

  parseEventDataEntries(ev: IdentityVerificationEvent): [string, unknown][] {
    if (!ev?.data) return [];

    let data: any = ev.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return [];
      }
    }
    if (typeof data !== 'object') return [];

    return Object.entries(data).map(([k, v]) => {
      let display: unknown;
      if (v === null || v === undefined) display = '—';
      else if (typeof v === 'object') display = JSON.stringify(v, null, 2);
      else display = v;
      return [k, display] as [string, unknown];
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.currentDetail) {
      this.idVerificationSocket.leaveVerificationRoom(this.currentDetail.id);
    }
    if (this.client?.civilId) {
      this.idVerificationSocket.leaveClientRoom(
        this.client.civilId,
        this.client.brand.code
      );
    }
    this.service.identityVerificationDetail$.next(undefined);
  }
}
